import type { AssistantStepExecutorMap } from '@/features/assistant/orchestrator'
import type { VoiceoverVideoAssistantContext } from '@/features/assistant/planner/voiceover-video-planner'
import { executeAssistantTool } from '@/features/assistant/tool-registry'
import type { AssistantArtifact } from '@/features/assistant/types'
import {
  loadAssistantMediaAsset,
  saveGeneratedImageToProjectMedia,
} from '@/features/assistant/tools/media-tools'
import { generateReferenceImage } from '@/features/assistant/tools/image-tools'
import { useTimelineStore } from '@/features/editor/deps/timeline-store'
import {
  duplicateAssistantProject,
  getCurrentProject,
} from '@/features/assistant/tools/project-tools'
import {
  generateBackgroundMusic,
  loadBackgroundMusicFromMedia,
} from '@/features/assistant/tools/music-tools'
import {
  addDefaultTransitionsForVisualSequence,
  insertAudioToTimeline,
  insertLoopingAudioToTimeline,
  insertReferenceMediaSequenceToTimeline,
} from '@/features/assistant/tools/timeline-tools'
import {
  generateTtsAudio,
  saveGeneratedAudioToProjectMedia,
  type GenerateTtsAudioRequest,
} from '@/features/assistant/tools/tts-tools'
import {
  createTranscriptForGeneratedAudio,
  insertTranscriptAsCaptions,
} from '@/features/assistant/tools/transcription-tools'
import { startAssistantExport } from '@/features/assistant/tools/export-tools'
import { getAssistantFallbackDecision } from '@/features/assistant/fallback-policy'

export interface VoiceoverExecutorDeps {
  loadAssistantMediaAsset: typeof loadAssistantMediaAsset
  generateReferenceImage: typeof generateReferenceImage
  saveGeneratedImageToProjectMedia: typeof saveGeneratedImageToProjectMedia
  getCurrentProject: () => AssistantProjectSnapshotLike | Promise<AssistantProjectSnapshotLike>
  duplicateAssistantProject: typeof duplicateAssistantProject
  generateTtsAudio: typeof generateTtsAudio
  generateBackgroundMusic: typeof generateBackgroundMusic
  loadBackgroundMusicFromMedia: typeof loadBackgroundMusicFromMedia
  saveGeneratedAudioToProjectMedia: typeof saveGeneratedAudioToProjectMedia
  insertAudioToTimeline: typeof insertAudioToTimeline
  insertLoopingAudioToTimeline: typeof insertLoopingAudioToTimeline
  insertReferenceMediaSequenceToTimeline: typeof insertReferenceMediaSequenceToTimeline
  addDefaultTransitionsForVisualSequence: typeof addDefaultTransitionsForVisualSequence
  createTranscriptForGeneratedAudio: typeof createTranscriptForGeneratedAudio
  insertTranscriptAsCaptions: typeof insertTranscriptAsCaptions
  startAssistantExport: typeof startAssistantExport
}

type AssistantProjectSnapshotLike = ReturnType<typeof getCurrentProject>

async function executeRegisteredTool<TOutput>(
  name: string,
  input?: Record<string, unknown>,
): Promise<TOutput> {
  return (await executeAssistantTool(name, input ?? {})) as TOutput
}

function createRegistryBackedVoiceoverExecutorDeps(): Pick<
  VoiceoverExecutorDeps,
  | 'duplicateAssistantProject'
  | 'getCurrentProject'
  | 'generateTtsAudio'
  | 'saveGeneratedAudioToProjectMedia'
  | 'insertAudioToTimeline'
  | 'insertTranscriptAsCaptions'
  | 'startAssistantExport'
> {
  return {
    getCurrentProject: () =>
      executeRegisteredTool<ReturnType<typeof getCurrentProject>>('get_current_project'),
    duplicateAssistantProject,
    generateTtsAudio: (request) =>
      executeRegisteredTool<Awaited<ReturnType<typeof generateTtsAudio>>>(
        'generate_tts_audio',
        request,
      ),
    saveGeneratedAudioToProjectMedia: (file, projectId, options) =>
      executeRegisteredTool<Awaited<ReturnType<typeof saveGeneratedAudioToProjectMedia>>>(
        'save_generated_audio_to_media',
        {
          file,
          projectId,
          tags: options?.tags,
          codec: options?.codec,
        },
      ),
    insertAudioToTimeline: (media, blobUrl, options) =>
      executeRegisteredTool<Awaited<ReturnType<typeof insertAudioToTimeline>>>(
        'insert_audio_to_timeline',
        {
          media,
          blobUrl,
          volumeDb: options?.volumeDb,
        },
      ),
    insertTranscriptAsCaptions: (mediaId, options) =>
      executeRegisteredTool<Awaited<ReturnType<typeof insertTranscriptAsCaptions>>>(
        'insert_transcript_as_captions',
        {
          mediaId,
          clipIds: options?.clipIds ? [...options.clipIds] : undefined,
          replaceExisting: options?.replaceExisting,
          preferredTrackAboveTrackId: options?.preferredTrackAboveTrackId,
        },
      ),
    startAssistantExport: (request = {}) =>
      executeRegisteredTool<Awaited<ReturnType<typeof startAssistantExport>>>('start_export', {
        settings: request.settings as Record<string, unknown> | undefined,
        signal: request.signal,
        download: request.download,
        fileName: request.fileName,
        onProgress: request.onProgress,
      }),
  }
}

function createArtifact(
  kind: AssistantArtifact['kind'],
  label: string,
  refId?: string,
): AssistantArtifact {
  return {
    id: crypto.randomUUID(),
    kind,
    label,
    refId,
    createdAt: Date.now(),
  }
}

function buildTtsRequest(context: VoiceoverVideoAssistantContext): GenerateTtsAudioRequest {
  return {
    engine: 'aliyun',
    text: context.script,
    voice: context.ttsVoice ?? 'Cherry',
    languageType: 'Chinese',
  }
}

function normalizeVisualPromptText(value: string): string {
  return value
    .replace(/帮我生成.{0,24}(口播视频|视频草稿|竖版视频|短视频)/g, '')
    .replace(/请帮我.{0,24}(口播视频|视频草稿|竖版视频|短视频)/g, '')
    .replace(/^(大家好|你好|今天|接下来|现在|这一期|这期|下面|首先)[，,、\s]*/g, '')
    .replace(/(大家好|你好)[，,、\s]*/g, '')
    .replace(/我来给你|带你|帮你|给你快速|快速认识|快速了解/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

function inferVisualScene(
  topic: string,
  beat: string,
): {
  sceneType: string
  subject: string
  environment: string
  focus: string
} {
  const source = `${topic} ${beat}`

  if (/(快剪|fastcut|剪辑|字幕|配音|口播|短视频|视频工具)/i.test(source)) {
    return {
      sceneType: '口播讲解配图，真实拍摄感的短视频创作场景',
      subject: '一位内容创作者或讲解者，正在说明短视频制作流程',
      environment: '干净的办公桌或小型工作室，出现手机、笔记本、麦克风、耳机、分镜纸等真实道具',
      focus: '突出录制、剪辑、字幕、配音等创作环节，而不是展示大段文字或软件截图',
    }
  }

  if (/(教程|教学|讲解|说明|介绍)/.test(source)) {
    return {
      sceneType: '人物讲解配图，知识说明型场景',
      subject: '一位讲解者或演示者，主体清晰',
      environment: '简洁室内环境、桌面道具或与主题相关的真实物体',
      focus: '突出讲解主题相关的关键物件和动作，不做海报式排版',
    }
  }

  return {
    sceneType: '口播辅助配图，真实摄影感场景',
    subject: '单一明确主体，便于口播时快速理解画面含义',
    environment: '与主题相关的真实环境和少量关键道具',
    focus: '突出主题相关的核心场景或物体，不做图文海报',
  }
}

function inferShotDescriptor(index: number, targetCount: number): string {
  if (index === 0) {
    return '开场 establishing shot，中景或近景，主体清晰，第一眼能看懂视频在讲什么'
  }

  if (index === targetCount - 1) {
    return '结尾总结镜头，构图稳定，画面更干净，适合收束信息'
  }

  return '中段补充镜头，聚焦一个动作或一组相关道具，避免信息过载'
}

function inferVisualCue(topic: string, beat: string): string {
  const normalizedBeat = normalizeVisualPromptText(beat)
  const normalizedTopic = normalizeVisualPromptText(topic)

  if (/(剪辑|字幕|配音|口播|短视频|快剪|fastcut)/i.test(`${normalizedTopic} ${normalizedBeat}`)) {
    return '手机竖屏视频制作、桌面工作流、麦克风或耳机、创作者讲解状态'
  }

  return normalizedBeat || normalizedTopic || '与视频主题直接相关的真实物件和场景'
}

function buildReferenceImagePrompts(context: VoiceoverVideoAssistantContext): string[] {
  const targetCount = Math.min(Math.max(Number(context.generatedReferenceImageCount ?? 0), 0), 3)
  if (targetCount === 0) {
    return []
  }

  const sentences = String(context.script ?? '')
    .split(/[。！？!?\n]+/)
    .map((value) => value.trim())
    .filter((value) => value.length >= 6)

  const aspectRatioLabel =
    context.aspectRatio === '9:16'
      ? '竖版 9:16 构图，适合手机短视频口播画面'
      : context.aspectRatio === '16:9'
        ? '横版 16:9 构图，适合讲解视频与演示视频'
        : '方形 1:1 构图，适合信息流封面和通用配图'

  const toneLabel =
    context.tone === 'professional'
      ? '专业、干净、可信'
      : context.tone === 'friendly'
        ? '亲和、明亮、自然'
        : context.tone === 'energetic'
          ? '有活力、现代、明快'
          : context.tone === 'calm'
            ? '平静、克制、简洁'
            : '自然、写实、简洁'

  return Array.from({ length: targetCount }, (_, index) => {
    const beat = sentences[index] ?? sentences[sentences.length - 1] ?? String(context.topic ?? '')
    const normalizedTopic =
      normalizeVisualPromptText(String(context.topic ?? '').trim()) || '短视频主题讲解'
    const normalizedBeat = normalizeVisualPromptText(beat) || normalizedTopic
    const visualScene = inferVisualScene(normalizedTopic, normalizedBeat)
    const shotDescriptor = inferShotDescriptor(index, targetCount)
    const visualCue = inferVisualCue(normalizedTopic, normalizedBeat)

    return [
      '任务：为中文口播视频生成一张无字参考配图，用于讲解过程中插入的辅助画面。',
      `视频主题：${normalizedTopic}`,
      `视觉意图：${normalizedBeat}`,
      `场景类型：${visualScene.sceneType}`,
      `主体：${visualScene.subject}`,
      `环境与道具：${visualScene.environment}`,
      `镜头设计：${shotDescriptor}`,
      `重点线索：${visualCue}`,
      `视觉重点：${visualScene.focus}`,
      `风格：${toneLabel}，真实摄影感，生活化，干净克制。`,
      `构图：${aspectRatioLabel}`,
      '输出要求：单一核心画面，主体明显，适合做口播 B-roll 静帧，不要复杂信息版式。',
      '硬性限制：不要海报，不要封面，不要 PPT 页面，不要网页或软件界面截图，不要对白气泡，不要漫画，不要拼贴，不要分屏，不要图文排版。',
      '文字限制：画面中不要出现任何可读中文、英文、数字、Logo、水印、标题、按钮、UI 文案。',
    ].join(' ')
  })
}

function getScriptVisualUnits(script: string): string[] {
  const paragraphs = script
    .split(/\n+/)
    .map((value) => value.trim())
    .filter(Boolean)

  if (paragraphs.length >= 2) {
    return paragraphs
  }

  const sentences = script
    .split(/[。！？!?；;]+/)
    .map((value) => value.trim())
    .filter(Boolean)

  if (sentences.length >= 2) {
    return sentences
  }

  const normalized = script.replace(/\s+/g, ' ').trim()
  return normalized ? [normalized] : []
}

function buildReferenceSegmentWeights(
  context: VoiceoverVideoAssistantContext,
  referenceAssetCount: number,
): number[] | undefined {
  if (referenceAssetCount <= 0) {
    return undefined
  }

  const units = getScriptVisualUnits(String(context.script ?? ''))
  if (units.length === 0) {
    return undefined
  }

  if (referenceAssetCount === 1) {
    return [Math.max(units.join('').length, 1)]
  }

  if (units.length < referenceAssetCount) {
    return Array.from({ length: referenceAssetCount }, () => 1)
  }

  const unitWeights = units.map((value) => Math.max(value.length, 1))
  const totalWeight = unitWeights.reduce((sum, value) => sum + value, 0)
  if (!(totalWeight > 0)) {
    return Array.from({ length: referenceAssetCount }, () => 1)
  }

  const groupedWeights: number[] = []
  let currentGroupWeight = 0
  let cumulativeWeight = 0
  let boundaryIndex = 1

  for (let index = 0; index < unitWeights.length; index++) {
    const unitWeight = unitWeights[index] ?? 1
    currentGroupWeight += unitWeight
    cumulativeWeight += unitWeight

    const remainingUnits = unitWeights.length - index - 1
    const remainingGroups = referenceAssetCount - groupedWeights.length - 1
    const targetBoundary = (totalWeight * boundaryIndex) / referenceAssetCount
    const crossedBoundary =
      boundaryIndex < referenceAssetCount && cumulativeWeight >= targetBoundary
    const mustSplitForRemainingUnits = remainingUnits === remainingGroups

    if (
      (crossedBoundary || mustSplitForRemainingUnits) &&
      groupedWeights.length < referenceAssetCount - 1
    ) {
      groupedWeights.push(Math.max(currentGroupWeight, 1))
      currentGroupWeight = 0
      boundaryIndex += 1
    }
  }

  groupedWeights.push(Math.max(currentGroupWeight, 1))

  if (groupedWeights.length !== referenceAssetCount) {
    return Array.from({ length: referenceAssetCount }, () => 1)
  }

  return groupedWeights
}

function buildReferenceSegmentPlacements(
  context: VoiceoverVideoAssistantContext,
  referenceAssetCount: number,
): Array<{ startRatio: number; endRatio: number }> | undefined {
  if (referenceAssetCount <= 0) {
    return undefined
  }

  const units = getScriptVisualUnits(String(context.script ?? ''))
  if (units.length === 0) {
    return undefined
  }

  if (referenceAssetCount === 1) {
    return [{ startRatio: 0, endRatio: 1 }]
  }

  if (units.length < referenceAssetCount) {
    return undefined
  }

  const unitWeights = units.map((value) => Math.max(value.length, 1))
  const totalWeight = unitWeights.reduce((sum, value) => sum + value, 0)
  if (!(totalWeight > 0)) {
    return undefined
  }

  const placements: Array<{ startRatio: number; endRatio: number }> = []
  let currentGroupWeight = 0
  let cumulativeWeight = 0
  let currentGroupStartWeight = 0
  let boundaryIndex = 1

  for (let index = 0; index < unitWeights.length; index++) {
    const unitWeight = unitWeights[index] ?? 1
    currentGroupWeight += unitWeight
    cumulativeWeight += unitWeight

    const remainingUnits = unitWeights.length - index - 1
    const remainingGroups = referenceAssetCount - placements.length - 1
    const targetBoundary = (totalWeight * boundaryIndex) / referenceAssetCount
    const crossedBoundary =
      boundaryIndex < referenceAssetCount && cumulativeWeight >= targetBoundary
    const mustSplitForRemainingUnits = remainingUnits === remainingGroups

    if (
      (crossedBoundary || mustSplitForRemainingUnits) &&
      placements.length < referenceAssetCount - 1
    ) {
      const groupEndWeight = currentGroupStartWeight + currentGroupWeight
      placements.push({
        startRatio: currentGroupStartWeight / totalWeight,
        endRatio: groupEndWeight / totalWeight,
      })
      currentGroupStartWeight = groupEndWeight
      currentGroupWeight = 0
      boundaryIndex += 1
    }
  }

  const finalGroupEndWeight = currentGroupStartWeight + currentGroupWeight
  placements.push({
    startRatio: currentGroupStartWeight / totalWeight,
    endRatio: finalGroupEndWeight / totalWeight,
  })

  if (placements.length !== referenceAssetCount) {
    return undefined
  }

  return placements.map((placement, index) => ({
    startRatio: index === 0 ? 0 : placement.startRatio,
    endRatio: index === placements.length - 1 ? 1 : placement.endRatio,
  }))
}

function buildReferenceSegmentPlacementsFromTranscript(
  context: VoiceoverVideoAssistantContext,
  referenceAssetCount: number,
): Array<{ startRatio: number; endRatio: number }> | undefined {
  if (referenceAssetCount <= 0) {
    return undefined
  }

  const transcriptSegments = Array.isArray(context.transcriptSegments)
    ? context.transcriptSegments
        .map((segment) => ({
          start: Number(segment.start),
          end: Number(segment.end),
        }))
        .filter(
          (segment) =>
            Number.isFinite(segment.start) &&
            Number.isFinite(segment.end) &&
            segment.end > segment.start,
        )
    : []

  if (transcriptSegments.length === 0) {
    return undefined
  }

  const totalDurationSeconds = Math.max(
    Number(context.durationSeconds ?? 0),
    transcriptSegments[transcriptSegments.length - 1]?.end ?? 0,
    0.2,
  )

  if (referenceAssetCount === 1) {
    return [{ startRatio: 0, endRatio: 1 }]
  }

  if (transcriptSegments.length < referenceAssetCount) {
    return undefined
  }

  const segmentDurations = transcriptSegments.map((segment) =>
    Math.max(segment.end - segment.start, 0.2),
  )
  const totalTranscriptDuration = segmentDurations.reduce((sum, value) => sum + value, 0)
  if (!(totalTranscriptDuration > 0)) {
    return undefined
  }

  const placements: Array<{ startRatio: number; endRatio: number }> = []
  let currentGroupDuration = 0
  let cumulativeDuration = 0
  let currentGroupStartSegmentIndex = 0
  let boundaryIndex = 1

  for (let index = 0; index < segmentDurations.length; index++) {
    const segmentDuration = segmentDurations[index] ?? 0.2
    currentGroupDuration += segmentDuration
    cumulativeDuration += segmentDuration

    const remainingSegments = segmentDurations.length - index - 1
    const remainingGroups = referenceAssetCount - placements.length - 1
    const targetBoundary = (totalTranscriptDuration * boundaryIndex) / referenceAssetCount
    const crossedBoundary =
      boundaryIndex < referenceAssetCount && cumulativeDuration >= targetBoundary
    const mustSplitForRemainingSegments = remainingSegments === remainingGroups

    if (
      (crossedBoundary || mustSplitForRemainingSegments) &&
      placements.length < referenceAssetCount - 1
    ) {
      const groupStart = transcriptSegments[currentGroupStartSegmentIndex]?.start ?? 0
      const groupEnd = transcriptSegments[index]?.end ?? groupStart + currentGroupDuration
      placements.push({
        startRatio: groupStart / totalDurationSeconds,
        endRatio: groupEnd / totalDurationSeconds,
      })
      currentGroupDuration = 0
      currentGroupStartSegmentIndex = index + 1
      boundaryIndex += 1
    }
  }

  const finalGroupStart = transcriptSegments[currentGroupStartSegmentIndex]?.start ?? 0
  const finalGroupEnd =
    transcriptSegments[transcriptSegments.length - 1]?.end ?? totalDurationSeconds
  placements.push({
    startRatio: finalGroupStart / totalDurationSeconds,
    endRatio: finalGroupEnd / totalDurationSeconds,
  })

  if (placements.length !== referenceAssetCount) {
    return undefined
  }

  return placements.map((placement, index) => ({
    startRatio: index === 0 ? 0 : Math.min(Math.max(placement.startRatio, 0), 1),
    endRatio:
      index === placements.length - 1
        ? 1
        : Math.min(Math.max(placement.endRatio, placement.startRatio), 1),
  }))
}

function getAllReferenceMediaIds(context: VoiceoverVideoAssistantContext): string[] {
  const selectedReferenceMediaIds = Array.isArray(context.selectedReferenceMediaIds)
    ? context.selectedReferenceMediaIds.map((value) => String(value)).filter(Boolean)
    : []
  const generatedReferenceMediaIds = Array.isArray(context.generatedReferenceMediaIds)
    ? context.generatedReferenceMediaIds.map((value) => String(value)).filter(Boolean)
    : []

  return [...selectedReferenceMediaIds, ...generatedReferenceMediaIds]
}

export function createVoiceoverStepExecutors(
  deps: Partial<VoiceoverExecutorDeps> = {},
): AssistantStepExecutorMap<VoiceoverVideoAssistantContext> {
  const registryBackedDeps = createRegistryBackedVoiceoverExecutorDeps()
  const resolvedDeps: VoiceoverExecutorDeps = {
    loadAssistantMediaAsset,
    generateReferenceImage,
    saveGeneratedImageToProjectMedia,
    getCurrentProject: registryBackedDeps.getCurrentProject,
    duplicateAssistantProject: registryBackedDeps.duplicateAssistantProject,
    generateTtsAudio: registryBackedDeps.generateTtsAudio,
    generateBackgroundMusic,
    loadBackgroundMusicFromMedia,
    saveGeneratedAudioToProjectMedia: registryBackedDeps.saveGeneratedAudioToProjectMedia,
    insertAudioToTimeline: registryBackedDeps.insertAudioToTimeline,
    insertLoopingAudioToTimeline,
    insertReferenceMediaSequenceToTimeline,
    addDefaultTransitionsForVisualSequence,
    createTranscriptForGeneratedAudio,
    insertTranscriptAsCaptions: registryBackedDeps.insertTranscriptAsCaptions,
    startAssistantExport: registryBackedDeps.startAssistantExport,
    ...deps,
  }

  return {
    get_current_project: async ({ job, step }) => {
      const sourceProject = await resolvedDeps.getCurrentProject()
      const timelineStrategyMode =
        (step.payload?.timelineStrategyMode as string | undefined) ??
        job.context.timelineStrategy?.mode ??
        'reuse-current-timeline'
      const shouldDuplicateCurrentProject =
        timelineStrategyMode === 'create-draft-timeline' &&
        !job.context.executionProjectWasDuplicated

      const project = shouldDuplicateCurrentProject
        ? await resolvedDeps.duplicateAssistantProject(sourceProject.projectId)
        : sourceProject

      return {
        contextPatch: {
          projectId: project.projectId,
          mediaProjectId: project.mediaProjectId,
          projectName: project.name,
          sourceProjectId: sourceProject.projectId,
          sourceProjectName: sourceProject.name,
          executionProjectId: project.projectId,
          executionProjectName: project.name,
          executionProjectWasDuplicated: shouldDuplicateCurrentProject,
          width: project.width,
          height: project.height,
          fps: project.fps,
        },
        artifacts: [createArtifact('project', project.name, project.projectId)],
        logMessage: shouldDuplicateCurrentProject
          ? `Created and switched to draft project: ${project.name} (from ${sourceProject.name})`
          : `Attached to current project: ${project.name}`,
      }
    },
    select_reference_media: async ({ job }) => {
      const selectedReferenceMediaLabels = Array.isArray(job.context.selectedReferenceMediaLabels)
        ? job.context.selectedReferenceMediaLabels.map((value) => String(value)).filter(Boolean)
        : []

      return {
        contextPatch: {
          selectedReferenceMediaIds: Array.isArray(job.context.selectedReferenceMediaIds)
            ? job.context.selectedReferenceMediaIds
            : [],
          selectedReferenceMediaLabels,
        },
        logMessage:
          selectedReferenceMediaLabels.length > 0
            ? `Selected ${selectedReferenceMediaLabels.length} reference media item${selectedReferenceMediaLabels.length > 1 ? 's' : ''}: ${selectedReferenceMediaLabels.join(', ')}`
            : 'No reference media selected. The assistant will continue without visual references unless you choose some before execution.',
      }
    },
    generate_reference_images: async ({ job, reportProgress }) => {
      try {
        const prompts = buildReferenceImagePrompts(job.context)
        if (prompts.length === 0) {
          return {
            status: 'skipped',
            contextPatch: {
              generatedReferenceImageFiles: [],
              generatedReferenceImagePrompts: [],
              generatedReferenceImageCount: 0,
            },
            logMessage:
              'Reference image generation skipped because existing visual references are already available.',
          }
        }

        const generatedFiles: File[] = []
        const generatedPrompts: string[] = []
        const generatedDimensions: Array<{ width: number; height: number }> = []

        for (let index = 0; index < prompts.length; index++) {
          const prompt = prompts[index]
          if (!prompt) {
            continue
          }

          reportProgress({
            progressPercent: Math.round((index / prompts.length) * 100),
            progressLabel: `正在生成参考图片 ${index + 1}/${prompts.length}...`,
          })
          const result = await resolvedDeps.generateReferenceImage({
            prompt,
            aspectRatio: job.context.aspectRatio,
          })
          generatedFiles.push(result.file)
          generatedPrompts.push(result.prompt)
          generatedDimensions.push({ width: result.width, height: result.height })
        }

        return {
          contextPatch: {
            generatedReferenceImageFiles: generatedFiles,
            generatedReferenceImagePrompts: generatedPrompts,
            generatedReferenceImageDimensions: generatedDimensions,
            generatedReferenceImageCount: generatedFiles.length,
          },
          logMessage: `Generated ${generatedFiles.length} fallback reference image${generatedFiles.length > 1 ? 's' : ''}`,
        }
      } catch (error) {
        const decision = getAssistantFallbackDecision('generate_reference_images', error)
        if (decision.action === 'skip-step') {
          return {
            status: 'skipped',
            contextPatch: {
              generatedReferenceImageFiles: [],
              generatedReferenceImagePrompts: [],
              generatedReferenceImageCount: 0,
            },
            logMessage: decision.logMessage,
          }
        }
        throw error
      }
    },
    save_generated_images_to_media: async ({ job }) => {
      try {
        const mediaProjectId = String(job.context.mediaProjectId ?? '')
        const generatedFiles = Array.isArray(job.context.generatedReferenceImageFiles)
          ? job.context.generatedReferenceImageFiles.filter(
              (value): value is File => value instanceof File,
            )
          : []
        const generatedDimensions = Array.isArray(job.context.generatedReferenceImageDimensions)
          ? job.context.generatedReferenceImageDimensions
          : []

        if (!mediaProjectId || generatedFiles.length === 0) {
          return {
            status: 'skipped',
            contextPatch: {
              generatedReferenceMediaIds: [],
              generatedReferenceMediaLabels: [],
            },
            logMessage: 'No generated reference images to save to the media library.',
          }
        }

        const importedMedia = []
        for (let index = 0; index < generatedFiles.length; index++) {
          const file = generatedFiles[index]
          if (!file) {
            continue
          }

          const dimensions = generatedDimensions[index] as
            | { width?: number; height?: number }
            | undefined
          const media = await resolvedDeps.saveGeneratedImageToProjectMedia(file, mediaProjectId, {
            tags: ['ai-generated', 'assistant-reference-image', 'nvidia-image'],
            width: dimensions?.width,
            height: dimensions?.height,
          })
          importedMedia.push(media)
        }

        return {
          contextPatch: {
            generatedReferenceMediaIds: importedMedia.map((media) => media.id),
            generatedReferenceMediaLabels: importedMedia.map((media) => media.fileName),
          },
          artifacts: importedMedia.map((media) =>
            createArtifact('image', media.fileName, media.id),
          ),
          logMessage: `Saved ${importedMedia.length} generated reference image${importedMedia.length > 1 ? 's' : ''} to the media library`,
        }
      } catch (error) {
        const decision = getAssistantFallbackDecision('save_generated_images_to_media', error)
        if (decision.action === 'skip-step') {
          return {
            status: 'skipped',
            contextPatch: {
              generatedReferenceMediaIds: [],
              generatedReferenceMediaLabels: [],
            },
            logMessage: decision.logMessage,
          }
        }
        throw error
      }
    },
    prepare_materials: async ({ job, reportProgress }) => {
      const referenceMediaIds = getAllReferenceMediaIds(job.context)

      if (referenceMediaIds.length === 0) {
        return {
          contextPatch: {
            insertedReferenceItemIds: [],
            insertedReferenceItemCount: 0,
            insertedReferenceTransitionCount: 0,
          },
          logMessage:
            'No reference media available; material preparation completed without visual references.',
        }
      }

      const canvasWidth = Number(job.context.width ?? 0)
      const canvasHeight = Number(job.context.height ?? 0)
      const totalDurationSeconds = Number(job.context.durationSeconds ?? 0)
      if (!(canvasWidth > 0) || !(canvasHeight > 0) || !(totalDurationSeconds > 0)) {
        throw new Error('Missing project canvas or duration before placing reference media.')
      }

      const loadedAssets: Array<{
        media: Parameters<typeof insertReferenceMediaSequenceToTimeline>[0][number]['media']
        blobUrl: string
      }> = []
      for (let index = 0; index < referenceMediaIds.length; index++) {
        const mediaId = referenceMediaIds[index]
        if (!mediaId) {
          continue
        }

        reportProgress({
          progressPercent: Math.round((index / referenceMediaIds.length) * 70),
          progressLabel: `正在读取参考素材 ${index + 1}/${referenceMediaIds.length}...`,
        })
        const asset = await resolvedDeps.loadAssistantMediaAsset(mediaId)
        loadedAssets.push({
          media: asset.media,
          blobUrl: URL.createObjectURL(asset.blob),
        })
      }

      reportProgress({
        progressPercent: 85,
        progressLabel: '正在将参考素材插入时间线...',
      })
      const segmentWeights = buildReferenceSegmentWeights(job.context, loadedAssets.length)
      const segmentPlacements =
        buildReferenceSegmentPlacementsFromTranscript(job.context, loadedAssets.length) ??
        buildReferenceSegmentPlacements(job.context, loadedAssets.length)
      const result = await resolvedDeps.insertReferenceMediaSequenceToTimeline(loadedAssets, {
        totalDurationSeconds,
        canvasWidth,
        canvasHeight,
        segmentWeights,
        segmentPlacements,
      })

      if (!result.inserted || result.itemIds.length === 0) {
        throw new Error('Could not insert selected reference media onto the timeline.')
      }

      const transitionResult = await resolvedDeps.addDefaultTransitionsForVisualSequence(
        result.itemIds,
      )

      return {
        contextPatch: {
          insertedReferenceItemIds: result.itemIds,
          insertedReferenceItemCount: result.itemIds.length,
          insertedReferenceTransitionCount: transitionResult.addedTransitionCount,
        },
        artifacts: [createArtifact('timeline', 'Prepared reference media', result.itemIds[0])],
        logMessage:
          transitionResult.addedTransitionCount > 0
            ? `Prepared ${result.itemIds.length} reference visual items and ${transitionResult.addedTransitionCount} transitions on the timeline`
            : `Prepared ${result.itemIds.length} reference visual items on the timeline`,
      }
    },
    generate_tts_audio: async ({ job, reportProgress }) => {
      const result = await resolvedDeps.generateTtsAudio({
        ...buildTtsRequest(job.context),
        onProgress: (stage) => {
          reportProgress({
            progressLabel: stage,
          })
        },
      })
      return {
        contextPatch: {
          generatedAudioFile: result.file,
          generatedAudioBlobUrl: URL.createObjectURL(result.blob),
          generatedAudioDuration: result.duration,
          ttsEngine: result.engine,
        },
        logMessage: `Generated TTS audio with ${result.engine}`,
      }
    },
    save_generated_audio_to_media: async ({ job }) => {
      const mediaProjectId = String(job.context.mediaProjectId ?? '')
      const generatedAudioFile = job.context.generatedAudioFile
      if (!(generatedAudioFile instanceof File)) {
        throw new Error('Missing generated audio file in assistant context.')
      }

      const media = await resolvedDeps.saveGeneratedAudioToProjectMedia(
        generatedAudioFile,
        mediaProjectId,
        {
          tags: ['ai-generated', 'assistant-voiceover'],
        },
      )

      return {
        contextPatch: {
          generatedAudioMediaId: media.id,
          generatedAudioMedia: media,
        },
        artifacts: [createArtifact('audio', media.fileName, media.id)],
        logMessage: `Saved generated audio to media library: ${media.fileName}`,
      }
    },
    insert_audio_to_timeline: async ({ job }) => {
      const media = job.context.generatedAudioMedia
      const blobUrl = job.context.generatedAudioBlobUrl
      if (!media || typeof media !== 'object' || !('id' in media)) {
        throw new Error('Missing generated audio media in assistant context.')
      }
      if (typeof blobUrl !== 'string' || !blobUrl) {
        throw new Error('Missing generated audio blob URL in assistant context.')
      }

      const result = await resolvedDeps.insertAudioToTimeline(media as never, blobUrl)
      if (!result.inserted || !result.audioItemId) {
        throw new Error('Could not insert generated audio onto the timeline.')
      }

      return {
        contextPatch: {
          generatedAudioItemId: result.audioItemId,
        },
        artifacts: [createArtifact('timeline', 'Voiceover audio inserted', result.audioItemId)],
        logMessage: 'Inserted generated audio onto the timeline',
      }
    },
    transcribe_media: async ({ job }) => {
      const mediaId = String(job.context.generatedAudioMediaId ?? '')
      const script = String(job.context.script ?? '').trim()
      const durationSeconds = Number(job.context.generatedAudioDuration ?? 0)
      if (!mediaId) {
        throw new Error('Missing generated audio media id in assistant context.')
      }
      if (!script) {
        throw new Error('Missing generated voiceover script in assistant context.')
      }
      if (!(durationSeconds > 0)) {
        throw new Error('Missing generated audio duration in assistant context.')
      }

      const transcript = await resolvedDeps.createTranscriptForGeneratedAudio(mediaId, {
        text: script,
        durationSeconds,
        language: 'zh',
      })
      return {
        contextPatch: {
          transcriptSegmentCount: transcript.segments.length,
          transcriptSegments: transcript.segments,
        },
        artifacts: [createArtifact('transcript', 'Generated transcript', mediaId)],
        logMessage: `Generated transcript from voiceover script with ${transcript.segments.length} segments`,
      }
    },
    insert_transcript_as_captions: async ({ job }) => {
      const mediaId = String(job.context.generatedAudioMediaId ?? '')
      const clipId = String(job.context.generatedAudioItemId ?? '')
      if (!mediaId || !clipId) {
        throw new Error('Missing audio media or timeline item id before inserting captions.')
      }

      const result = await resolvedDeps.insertTranscriptAsCaptions(mediaId, {
        clipIds: [clipId],
        replaceExisting: true,
        preferredTrackAboveTrackId: (() => {
          const referenceItemIds = Array.isArray(job.context.insertedReferenceItemIds)
            ? job.context.insertedReferenceItemIds.map((value) => String(value)).filter(Boolean)
            : []
          if (referenceItemIds.length === 0) {
            return undefined
          }

          const timeline = useTimelineStore.getState()
          const referenceItem = timeline.items.find((item) => item.id === referenceItemIds[0])
          return referenceItem?.trackId
        })(),
      })

      return {
        contextPatch: {
          insertedCaptionCount: result.insertedItemCount,
        },
        artifacts: [createArtifact('captions', 'Inserted transcript captions', mediaId)],
        logMessage: `Inserted ${result.insertedItemCount} caption items`,
      }
    },
    generate_bgm: async ({ job, signal, reportProgress }) => {
      try {
        const mediaProjectId = String(job.context.mediaProjectId ?? '')
        const topic = String(job.context.topic ?? '').trim()
        const tone = job.context.tone
        const bgmMode = job.context.bgmMode ?? 'generate'
        const selectedBgmMediaId = String(job.context.selectedBgmMediaId ?? '')
        const requestedDurationSeconds = Number(job.context.durationSeconds ?? 0)
        if (!mediaProjectId) {
          throw new Error('Missing project id before generating background music.')
        }
        if (!topic) {
          throw new Error('Missing topic before generating background music.')
        }
        if (!tone) {
          throw new Error('Missing tone before generating background music.')
        }

        if (bgmMode === 'selected') {
          if (!selectedBgmMediaId) {
            throw new Error('当前没有选中的背景音乐素材。')
          }

          reportProgress({
            progressPercent: 20,
            progressLabel: '正在读取已选背景音乐素材...',
          })
          const { media, blob } =
            await resolvedDeps.loadBackgroundMusicFromMedia(selectedBgmMediaId)

          return {
            contextPatch: {
              generatedBgmBlobUrl: URL.createObjectURL(blob),
              generatedBgmDuration: media.duration,
              generatedBgmPrompt: null,
              generatedBgmModel: 'selected-media',
              generatedBgmMediaId: media.id,
              generatedBgmMedia: media,
              generatedBgmCoverageDuration: Math.max(requestedDurationSeconds || 0, media.duration),
            },
            artifacts: [createArtifact('music', media.fileName, media.id)],
            logMessage: `Using selected background music: ${media.fileName}`,
          }
        }

        const targetDurationSeconds = Math.min(Math.max(requestedDurationSeconds || 8, 2), 8)
        const result = await resolvedDeps.generateBackgroundMusic({
          topic,
          tone,
          durationSeconds: targetDurationSeconds,
          onProgress: (stage, fraction) => {
            reportProgress({
              progressPercent:
                typeof fraction === 'number' ? Math.round(fraction * 100) : undefined,
              progressLabel: stage,
            })
          },
          signal,
        })

        const media = await resolvedDeps.saveGeneratedAudioToProjectMedia(
          result.file,
          mediaProjectId,
          {
            tags: [
              'ai-generated',
              'assistant-bgm',
              'aliyun-music',
              `aliyun-music-model:${result.model}`,
              'music-instrumental:true',
            ],
          },
        )

        return {
          contextPatch: {
            generatedBgmFile: result.file,
            generatedBgmBlobUrl: URL.createObjectURL(result.blob),
            generatedBgmDuration: result.duration,
            generatedBgmPrompt: result.prompt,
            generatedBgmModel: result.model,
            generatedBgmMediaId: media.id,
            generatedBgmMedia: media,
            generatedBgmCoverageDuration: Math.max(requestedDurationSeconds || 0, result.duration),
          },
          artifacts: [createArtifact('music', media.fileName, media.id)],
          logMessage: `Generated background music: ${media.fileName}`,
        }
      } catch (error) {
        const decision = getAssistantFallbackDecision('generate_bgm', error)
        if (decision.action === 'skip-step') {
          return {
            status: 'skipped',
            contextPatch: {
              bgmMode: 'none',
            },
            logMessage: decision.logMessage,
          }
        }
        throw error
      }
    },
    attach_bgm_to_timeline: async ({ job }) => {
      const media = job.context.generatedBgmMedia
      const blobUrl = job.context.generatedBgmBlobUrl
      if (!media || typeof media !== 'object' || !('id' in media)) {
        throw new Error('Missing generated background music media in assistant context.')
      }
      if (typeof blobUrl !== 'string' || !blobUrl) {
        throw new Error('Missing generated background music blob URL in assistant context.')
      }
      const totalDurationSeconds = Number(
        job.context.generatedBgmCoverageDuration ?? job.context.durationSeconds ?? 0,
      )

      const result = await resolvedDeps.insertLoopingAudioToTimeline(media as never, blobUrl, {
        totalDurationSeconds,
        volumeDb: -18,
        placement: 'new-bottom-track',
      })
      if (!result.inserted || result.audioItemIds.length === 0) {
        throw new Error('Could not insert generated background music onto the timeline.')
      }

      return {
        contextPatch: {
          generatedBgmItemId: result.audioItemIds[0],
          generatedBgmItemCount: result.audioItemIds.length,
        },
        artifacts: [
          createArtifact('timeline', 'Background music inserted', result.audioItemIds[0]),
        ],
        logMessage: `Inserted looping background music onto the timeline (${result.audioItemIds.length} items)`,
      }
    },
    start_export: async ({ job, signal, reportProgress }) => {
      try {
        reportProgress({
          progressPercent: 10,
          progressLabel: '正在准备导出草稿...',
        })

        const exportResult = await resolvedDeps.startAssistantExport({
          signal,
          onProgress: (progress) => {
            reportProgress({
              progressPercent: progress.progress,
              progressLabel: progress.message ?? progress.phase,
            })
          },
          fileName: `${String(job.context.projectName ?? 'assistant-draft')}-draft`,
        })

        return {
          contextPatch: {
            exportFileName: exportResult.fileName,
            exportMimeType: exportResult.mimeType,
            exportFileSize: exportResult.fileSize,
          },
          artifacts: [
            {
              ...createArtifact('export', exportResult.fileName),
              data: {
                mimeType: exportResult.mimeType,
                fileSize: exportResult.fileSize,
                duration: exportResult.duration,
              },
            },
          ],
          logMessage: `Exported draft: ${exportResult.fileName} (${exportResult.fileSizeLabel})`,
        }
      } catch (error) {
        const decision = getAssistantFallbackDecision('start_export', error)
        if (decision.action === 'skip-step') {
          return {
            status: 'skipped',
            logMessage: decision.logMessage,
          }
        }
        throw error
      }
    },
  }
}
